import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataImportColumnMapping, IDataImportField } from "../DataImport.interfaces";
import { getConfidenceColor } from "../helpers/DataInporter.helpers";

interface ColumnMappingPanelProps {
  fields: IDataImportField[];
  csvHeaders: string[];
  mappings: DataImportColumnMapping[];
  onMappingChange: (fieldKey: string, event: SelectChangeEvent<string | string[]>) => void;
  disabled?: boolean;
}

export function ColumnMappingPanel({ fields, csvHeaders, mappings, onMappingChange, disabled }: ColumnMappingPanelProps) {
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
      <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 1.5, borderBottom: "1px solid", borderColor: "divider" } }}>
        <TableHead sx={{ bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)" }}>
          <TableRow>
            <TableCell sx={{ width: "40%" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                System Field
              </Typography>
            </TableCell>
            <TableCell sx={{ width: "45%" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                CSV Header
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ width: "15%", display: { xs: "none", sm: "table-cell" } }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                Status
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field) => {
            const mapping = mappings.find((item) => item.fieldKey === field.key);
            const confidenceColor = getConfidenceColor(mapping?.confidence ?? 0);
            const isMapped = !!mapping?.csvHeaders && mapping.csvHeaders.length > 0;
            const hasError = field.required && !isMapped;

            return (
              <TableRow
                key={field.key}
                sx={{
                  bgcolor: hasError ? "error.lighter" : "inherit",
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                {/* Field Label */}
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={hasError ? "error.main" : "text.primary"}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {field.label} {field.required && <span style={{ color: "#d32f2f" }}>*</span>}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                    key: {field.key}
                  </Typography>
                </TableCell>

                {/* Compact Select */}
                <TableCell>
                  <FormControl size="small" fullWidth hiddenLabel error={hasError}>
                    <Select
                      multiple
                      value={mapping?.csvHeaders ?? []}
                      onChange={(event) => onMappingChange(field.key, event)}
                      disabled={disabled}
                      displayEmpty
                      renderValue={(selected) => {
                        const selectedArray = selected as string[];
                        if (selectedArray.length === 0) {
                          return <em style={{ color: "var(--fx-surface-texttertiary, #94A3B8)", fontSize: "0.8125rem" }}>Select columns...</em>;
                        }
                        return selectedArray.join(", ");
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 224,
                            width: 250,
                          },
                        },
                      }}
                      sx={{
                        height: 30, // Ultra-compact height
                        fontSize: "0.8125rem",
                        bgcolor: "background.paper",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: isMapped ? "divider" : hasError ? "error.main" : "divider",
                        },
                      }}
                    >
                      {csvHeaders.map((header) => {
                        const isChecked = (mapping?.csvHeaders ?? []).indexOf(header) > -1;
                        return (
                          <MenuItem key={header} value={header} sx={{ fontSize: "0.8125rem" }}>
                            <Checkbox checked={isChecked} size="small" sx={{ p: 0.5 }} />
                            <ListItemText primary={header} primaryTypographyProps={{ fontSize: "0.8125rem" }} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </TableCell>

                {/* Status Dot */}
                <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  {isMapped ? (
                    <Tooltip title={`${Math.round(mapping.confidence * 100)}% automatic match`} placement="left">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: `${confidenceColor}.main` }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: "0.7rem" }}>
                          {mapping.matchedBy === "manual" ? "Verify" : "Complete"}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic", fontSize: "0.7rem" }}>
                      Empty
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
